import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParallax } from "@/hooks/useParallax";
import CountUp from "@/components/feature/CountUp";

interface FeatureItem {
  icon: string;
  title: string;
  description: string;
  stat: string;
  statLabel: string;
  statEnd?: number;
  statPrefix?: string;
  statSuffix?: string;
  statDecimals?: number;
}

const capModalTypes = ["cs-cap-1", "cs-cap-2", "cs-cap-3", "cs-cap-4", "cs-cap-5", "cs-cap-6"];

export default function Features({ onOpenModal }: { onOpenModal?: (type: string) => void }) {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { ref: bgRef, offset } = useParallax(0.07);

  const features = useMemo<FeatureItem[]>(() => [
    {
      icon: "ri-radar-line",
      title: t("cs.features.f1.title"),
      description: t("cs.features.f1.desc"),
      stat: t("cs.features.f1.stat"),
      statLabel: t("cs.features.f1.statLabel"),
      statEnd: 99.97,
      statDecimals: 2,
      statSuffix: "%",
    },
    {
      icon: "ri-shield-flash-line",
      title: t("cs.features.f2.title"),
      description: t("cs.features.f2.desc"),
      stat: t("cs.features.f2.stat"),
      statLabel: t("cs.features.f2.statLabel"),
      statEnd: 5,
      statPrefix: "< ",
      statSuffix: " min",
    },
    {
      icon: "ri-spy-line",
      title: t("cs.features.f3.title"),
      description: t("cs.features.f3.desc"),
      stat: t("cs.features.f3.stat"),
      statLabel: t("cs.features.f3.statLabel"),
      statEnd: 200,
      statPrefix: "+",
    },
    {
      icon: "ri-file-shield-line",
      title: t("cs.features.f4.title"),
      description: t("cs.features.f4.desc"),
      stat: t("cs.features.f4.stat"),
      statLabel: t("cs.features.f4.statLabel"),
    },
    {
      icon: "ri-computer-line",
      title: t("cs.features.f5.title"),
      description: t("cs.features.f5.desc"),
      stat: t("cs.features.f5.stat"),
      statLabel: t("cs.features.f5.statLabel"),
      statEnd: 12,
      statSuffix: "K+",
    },
    {
      icon: "ri-cloud-line",
      title: t("cs.features.f6.title"),
      description: t("cs.features.f6.desc"),
      stat: t("cs.features.f6.stat"),
      statLabel: t("cs.features.f6.statLabel"),
    },
  ], [t]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      {/* Humanized background with subtle parallax */}
      <div ref={bgRef} className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Security%20specialists%20reviewing%20threat%20dashboards%20together%20in%20a%20modern%20dark%20office%2C%20warm%20human%20atmosphere%2C%20professional%20corporate%20photography%2C%20human%20centered%2C%20soft%20depth%20of%20field&width=900&height=506&seq=cs-features-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="absolute -top-[10%] left-0 w-full h-[120%] object-cover object-center opacity-[0.14]"
          style={{ transform: `translateY(${offset}px)` }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950 via-background-950/40 to-background-950" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-background-950 to-transparent z-[5] pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
            <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
              {t("cs.features.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3 tracking-tight">
            {t("cs.features.title")} <span className="text-accent-400">{t("cs.features.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("cs.features.subtitle")}
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, idx) => (
            <button
              type="button"
              key={feature.title}
              onClick={() => onOpenModal?.(capModalTypes[idx])}
              className={`group relative text-left w-full cursor-pointer rounded-2xl border border-foreground-100/8 bg-background-950/50 backdrop-blur-sm p-6 overflow-hidden hover:border-primary-400/25 hover:-translate-y-1.5 hover:bg-background-900/50 transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${150 + idx * 90}ms` }}
            >
              <span className="absolute top-3 right-5 font-heading font-bold text-5xl text-foreground-100/[0.035] group-hover:text-primary-400/10 transition-colors duration-500 select-none">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500" />

              <div className="relative z-10">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-400/15 text-primary-400 mb-5 group-hover:bg-primary-500/20 group-hover:text-primary-300 group-hover:scale-105 transition-all duration-300">
                  <i className={`${feature.icon} text-xl`} />
                </div>

                <h3 className="font-heading font-semibold text-base text-foreground-100 mb-2 group-hover:text-primary-300 transition-colors">
                  {feature.title}
                </h3>

                <p className="text-sm text-foreground-400/60 leading-relaxed mb-5">
                  {feature.description}
                </p>

                <div className="flex items-center gap-2 pt-4 border-t border-foreground-100/8">
                  <span className="font-heading font-bold text-lg text-primary-400">
                    {typeof feature.statEnd === "number" ? (
                      <CountUp
                        end={feature.statEnd}
                        prefix={feature.statPrefix}
                        suffix={feature.statSuffix}
                        decimals={feature.statDecimals}
                      />
                    ) : (
                      feature.stat
                    )}
                  </span>
                  <span className="text-[10px] font-label text-foreground-500/40 uppercase tracking-wider">{feature.statLabel}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}